import { onBeforeUnmount, watch } from 'vue';
import type { WatchSource } from 'vue';
import type {
  LocationQuery,
  LocationQueryRaw,
  RouteLocationNormalizedLoaded,
  Router,
} from 'vue-router';
import isEqual from 'fast-deep-equal';

export type ListRouteQueryValue = string | number | boolean | null | undefined;

export type ListRouteQueryRecord = Record<
  string,
  ListRouteQueryValue | ListRouteQueryValue[]
>;

export interface ListRouteQueryParam<T> {
  parse: (query: LocationQuery, key: string) => T;
  serialize: (value: T) => ListRouteQueryValue | ListRouteQueryValue[];
}

export type ListRouteQueryParams<T extends object> = {
  [K in keyof T]-?: ListRouteQueryParam<T[K]>;
};

export const EMPTY_ARRAY_QUERY_VALUE = '__empty__';

export interface ListRouteStateCodec<TState> {
  parse: (query: LocationQuery) => TState;
  serialize: (state: TState) => ListRouteQueryRecord;
}

export interface UseListRouteStateOptions<TState> {
  route: RouteLocationNormalizedLoaded;
  router: Router;
  codec: ListRouteStateCodec<TState>;
  getState: () => TState;
  applyState: (state: TState) => void;
  watchSources: WatchSource[];
  debounceMs?: number;
}

const queryValueSignature = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${[...value].map(queryValueSignature).sort().join(',')}]`;
  }

  return JSON.stringify(value);
};

export const queriesEqual = (
  left: LocationQuery | LocationQueryRaw,
  right: LocationQuery | LocationQueryRaw,
): boolean => {
  const keys = [...new Set([...Object.keys(left), ...Object.keys(right)])].sort();

  return keys.every(key => queryValueSignature(left[key]) === queryValueSignature(right[key]));
};

export const getQueryValues = (query: LocationQuery, key: string): string[] => {
  const value = query[key];
  if (value == null) return [];

  return (Array.isArray(value) ? value : [value])
    .filter((entry): entry is string => typeof entry === 'string');
};

export const getQueryValue = (query: LocationQuery, key: string): string | undefined =>
  getQueryValues(query, key)[0];

export const parsePositiveInteger = (
  query: LocationQuery,
  key: string,
  defaultValue: number,
): number => {
  const value = Number(getQueryValue(query, key));
  return Number.isInteger(value) && value > 0 ? value : defaultValue;
};

export const parseOptionalBoolean = (
  query: LocationQuery,
  key: string,
): boolean | null => {
  const value = getQueryValue(query, key);
  return value === 'true' ? true : value === 'false' ? false : null;
};

export const parseOptionalString = (
  query: LocationQuery,
  key: string,
): string | null => getQueryValue(query, key) ?? null;

export const parseEnumArray = <T extends string>(
  query: LocationQuery,
  key: string,
  allowedValues: readonly T[],
): T[] | null => {
  if (getQueryValues(query, key).includes(EMPTY_ARRAY_QUERY_VALUE)) return [];

  const allowed = new Set(allowedValues);
  const values = getQueryValues(query, key).filter((value): value is T => allowed.has(value as T));
  return values.length > 0 ? values : null;
};

export const stringQueryParam = (): ListRouteQueryParam<string | null> => ({
  parse: (query, key) => parseOptionalString(query, key),
  serialize: value => value,
});

export const booleanQueryParam = (): ListRouteQueryParam<boolean | null> => ({
  parse: (query, key) => parseOptionalBoolean(query, key),
  serialize: value => value,
});

export const integerQueryParam = (
  defaultValue: number | null = null,
): ListRouteQueryParam<number | null> => ({
  parse: (query, key) => {
    const value = Number(getQueryValue(query, key));
    return Number.isInteger(value) && value > 0 ? value : defaultValue;
  },
  serialize: value => value,
});

export const integerArrayQueryParam = (): ListRouteQueryParam<number[] | null> => ({
  parse: (query, key) => {
    const values = getQueryValues(query, key);
    if (values.includes(EMPTY_ARRAY_QUERY_VALUE)) return [];

    const parsed = values
      .map(value => Number(value))
      .filter(value => Number.isInteger(value) && value > 0);
    return parsed.length > 0 ? parsed : null;
  },
  serialize: value => value,
});

export const enumQueryParam = <T extends string>(
  allowedValues: readonly T[],
): ListRouteQueryParam<T | null> => ({
  parse: (query, key) => {
    const value = getQueryValue(query, key);
    return value && allowedValues.includes(value as T) ? value as T : null;
  },
  serialize: value => value,
});

export const enumArrayQueryParam = <T extends string>(
  allowedValues: readonly T[],
  options: {
    defaultValue?: T[] | null;
    omitWhen?: (value: T[] | null) => boolean;
  } = {},
): ListRouteQueryParam<T[] | null> => ({
  parse: (query, key) => parseEnumArray(query, key, allowedValues) ?? options.defaultValue ?? null,
  serialize: value => options.omitWhen?.(value) ? undefined : value,
});

export const createListRouteStateCodec = <TState extends object>(
  params: ListRouteQueryParams<TState>,
): ListRouteStateCodec<TState> => ({
  parse: query => {
    const state = {} as TState;
    for (const key of Object.keys(params) as (keyof TState)[]) {
      const param = params[key];
      state[key] = param.parse(query, String(key)) as TState[typeof key];
    }
    return state;
  },
  serialize: state => {
    const query: ListRouteQueryRecord = {};
    for (const key of Object.keys(params) as (keyof TState)[]) {
      const param = params[key];
      query[String(key)] = param.serialize(state[key]);
    }
    return query;
  },
});

export const mergeListRouteQuery = (
  query: LocationQuery,
  stateQuery: ListRouteQueryRecord,
): LocationQueryRaw => {
  const stateKeys = new Set(Object.keys(stateQuery));
  const merged: LocationQueryRaw = Object.fromEntries(
    Object.entries(query).filter(([key]) => !stateKeys.has(key)),
  );

  for (const [key, value] of Object.entries(stateQuery)) {
    if (value === undefined || value === null || value === '') continue;

    if (Array.isArray(value)) {
      const filteredValue = value.filter(entry => entry !== undefined && entry !== null && entry !== '');
      merged[key] = filteredValue.length > 0
        ? filteredValue as string[]
        : [EMPTY_ARRAY_QUERY_VALUE];
    } else {
      merged[key] = value as string;
    }
  }

  return merged;
};

export const useListRouteState = <TState>({
  route,
  router,
  codec,
  getState,
  applyState,
  watchSources,
  debounceMs = 50,
}: UseListRouteStateOptions<TState>) => {
  let debounceHandle: ReturnType<typeof setTimeout> | undefined;
  let disposed = false;

  const restoreFromRoute = () => {
    const nextState = codec.parse(route.query);

    if (!isEqual(getState(), nextState)) {
      applyState(nextState);
    }
  };

  const syncToRoute = async () => {
    if (disposed) return;

    const nextQuery = mergeListRouteQuery(
      route.query,
      codec.serialize(getState()),
    );

    if (!queriesEqual(route.query, nextQuery)) {
      await router.replace({ query: nextQuery });
    }
  };

  const scheduleSync = () => {
    if (debounceHandle) clearTimeout(debounceHandle);
    debounceHandle = setTimeout(() => {
      void syncToRoute();
    }, debounceMs);
  };

  restoreFromRoute();

  const stopStateWatch = watch(watchSources, scheduleSync, { deep: true });
  const stopRouteWatch = watch(() => route.query, restoreFromRoute, { deep: true });

  onBeforeUnmount(() => {
    disposed = true;
    if (debounceHandle) clearTimeout(debounceHandle);
    stopStateWatch();
    stopRouteWatch();
  });

  return {
    restoreFromRoute,
    syncToRoute,
  };
};
